import React from 'react';
import { clsx } from 'clsx';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger' | 'premium' | 'roast';
  size?: 'sm' | 'md' | 'lg';
  icon?: React.ReactNode;
  isLoading?: boolean;
  children: React.ReactNode;
}

const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  icon,
  isLoading = false,
  children,
  className,
  disabled,
  ...props
}) => {
  const baseClasses = `
    inline-flex items-center justify-center gap-2 
    font-medium rounded-lg transition-all duration-200 ease-out
    focus:outline-none focus:ring-2 focus:ring-accent-primary/50 focus:ring-offset-2 focus:ring-offset-background-primary
    disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none
    interactive-element
  `;

  const sizeClasses = {
    sm: 'px-3 py-2 text-sm',
    md: 'px-4 py-2.5 text-sm',
    lg: 'px-6 py-3 text-base'
  };

  const variantClasses = {
    primary: `
      bg-gradient-to-r from-accent-primary to-accent-primary/90
      text-background-primary font-semibold
      hover:from-accent-primary/90 hover:to-accent-primary
      hover:shadow-lg hover:shadow-accent-primary/25
      hover:-translate-y-0.5
      border border-accent-primary/20
    `,
    secondary: `
      bg-white/10 text-text-primary
      border border-white/20
      backdrop-blur-sm
      hover:bg-white/15 hover:border-white/30
      hover:-translate-y-0.5
      hover:shadow-lg hover:shadow-black/10
    `,
    ghost: `
      text-text-secondary
      hover:text-text-primary hover:bg-white/5
      border border-transparent
      hover:border-white/10
    `,
    danger: `
      bg-gradient-to-r from-accent-danger to-accent-danger/90
      text-white font-semibold
      hover:from-accent-danger/90 hover:to-accent-danger
      hover:shadow-lg hover:shadow-accent-danger/25
      hover:-translate-y-0.5
      border border-accent-danger/20
    `,
    premium: `
      bg-accent-primary
      text-background-primary font-semibold
      hover:bg-accent-primary/90
      active:translate-y-px
      border border-accent-primary/30
    `,
    roast: `
      bg-background-tertiary text-accent-warning font-semibold
      border border-accent-warning/30
      hover:bg-accent-warning/10 hover:border-accent-warning/50
      active:translate-y-px
    `
  };

  // Three pulsing dots — no circular spinners in this design system
  const loadingSpinner = (
    <span className="inline-flex items-center gap-0.5" aria-label="Loading">
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="w-1 h-1 rounded-full bg-current animate-pulse"
          style={{ animationDelay: `${i * 150}ms` }}
        />
      ))}
    </span>
  );

  return (
    <button
      className={clsx(
        baseClasses,
        sizeClasses[size],
        variantClasses[variant],
        className
      )}
      disabled={disabled || isLoading}
      {...props}
    >
      {isLoading ? loadingSpinner : icon}
      {children}
    </button>
  );
};

export default Button; 